import { CONFIG } from "@component/Mortgage/Calculator/data";
import type { AbstractSubmitStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { AbstractSubmitStep } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import {
	faHouse as fasHouse,
	faMaximize as fasMaximize,
	faSuitcaseMedical as fasSuitcaseMedical,
	faArrowRight as fasArrowRight,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCurrency, FormDecidable, FormSelect, ProgressButton } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import React from "react";
import { Card } from "react-bootstrap";
import { isBuildingGoal } from "../domain";
import type { LoanGoal } from "./Step2.data";

const PLANNED_BUDGET_MIN = 500_000;
const PLANNED_BUDGET_MAX = 100_000_000;

export interface StepFields {
	realEstateValue: number;
	realEstateSizeId: number;
	ownRealEstateEnlargement: boolean;
	buyFromCloseRelative: boolean;
	earlierBoughtRealEstate: boolean;
	generalContractor: boolean;
	realEstateBuyings: number;
	plannedBudget: number;
	hasMoreThenHalfOwnership: boolean;
}

interface Props extends AbstractSubmitStepProps {
	realEstateSizes: CommonOptions<number>;
	realEstateBuyings: CommonOptions<number>;
	loanGoal: LoanGoal;
}

export class Step3 extends AbstractSubmitStep<StepFields, Props> {
	protected initValues(): StepFields {
		return {
			realEstateValue: undefined,
			realEstateSizeId: undefined,
			ownRealEstateEnlargement: undefined,
			buyFromCloseRelative: undefined,
			earlierBoughtRealEstate: undefined,
			generalContractor: undefined,
			realEstateBuyings: undefined,
			plannedBudget: undefined,
			hasMoreThenHalfOwnership: undefined,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { realEstateSizes, loanGoal, realEstateBuyings, submitState } = this.props;
		const isBuilding = isBuildingGoal(loanGoal);
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Body>
					<FormCurrency
						label={isBuilding ? "Mennyi az építés költsége?" : "Mennyi az ingatlan értéke?"}
						name="realEstateValue"
						placeholder={isBuilding ? "Építési költség" : "Ingatlan érték"}
						prepend={<FontAwesomeIcon icon={fasHouse} />}
						validate={this.validRealEstateValue}
						disabled={disabled}
						required
					/>
					<FormSelect
						label="Mekkora az ingatlan alapterülete?"
						name="realEstateSizeId"
						placeholder="Kérjük, válassz!"
						prepend={<FontAwesomeIcon icon={fasMaximize} />}
						options={realEstateSizes}
						disabled={disabled}
						required
					/>
					{loanGoal === "BOVITES" && (
						<FormDecidable
							label="Saját ingatlanodat bővíted a CSOK-kal?"
							name="ownRealEstateEnlargement"
							disabled={disabled}
							required
						/>
					)}
					{["UJ_LAKAS", "HASZNALT_LAKAS"].includes(loanGoal) && (
						<>
							<FormDecidable
								label="Közeli hozzátartozódtól, élettársadtól vásárolsz ingatlant?"
								name="buyFromCloseRelative"
								infoTooltip="Közeli hozzátartozónak számít a szülő, nagyszülő, gyermek (akár örökbefogadott), unoka, testvér és a házastárs is."
								disabled={disabled}
								required
							/>
							<FormDecidable
								label="Az adás-vételi szerződést több, mint 180 napja kötötted?"
								name="earlierBoughtRealEstate"
								disabled={disabled}
								required
							/>
						</>
					)}
					{isBuilding && (
						<FormDecidable
							label="Generálkivitelezővel építkeznél?"
							name="generalContractor"
							disabled={disabled}
							required
						/>
					)}
					<FormSelect
						label={this.getRealEstateBuyingsTitle()}
						name="realEstateBuyings"
						placeholder="Kérjük, válassz!"
						prepend={<FontAwesomeIcon icon={fasSuitcaseMedical} />}
						options={realEstateBuyings}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
						state={submitState}
					>
						Tovább
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validRealEstateValue(realEstateValue: number): string {
		const { loanGoal } = this.props;
		const isBuilding = isBuildingGoal(loanGoal);

		if (realEstateValue < CONFIG.MIN_REAL_ESTATE_VALUE) {
			return `${
				isBuilding ? "Az építkezés minimális költsége" : "A vásárolni kívánt ingatlan minimális értéke"
			} ${formatText(CONFIG.MIN_REAL_ESTATE_VALUE, "ft")}!`;
		}

		if (realEstateValue > CONFIG.MAX_REAL_ESTATE_VALUE) {
			return `${
				isBuilding ? "Az építkezés maximális költsége" : "A vásárolni kívánt ingatlan maximális értéke"
			} ${formatText(CONFIG.MAX_REAL_ESTATE_VALUE, "ft")}!`;
		}

		return undefined;
	}

	@autobind
	private validPlannedBudget(plannedBudget: number): string {
		if (plannedBudget < PLANNED_BUDGET_MIN) {
			return `A vásárolni kívánt ingatlan minimális értéke ${formatText(PLANNED_BUDGET_MIN, "ft")}!`;
		}

		if (plannedBudget > PLANNED_BUDGET_MAX) {
			return `A vásárolni kívánt ingatlan maximális értéke ${formatText(PLANNED_BUDGET_MAX, "ft")}!`;
		}

		return undefined;
	}

	private getRealEstateBuyingsTitle(): string {
		const { loanGoal } = this.props;

		switch (loanGoal) {
			case "EPITES":
				return "Mikor szeretnéd elkezdeni az építkezést?";
			case "BOVITES":
				return "Mikor szeretnéd elkezdeni a bővítést?";
			case "TETOTER_BEEPITES":
				return "Mikor szeretnéd elkezdeni a tetőtér-beépítést?";
			default:
				return "Kiválasztottad már a lakást/házat?";
		}
	}

	protected getComponentName(): string {
		return "Step3";
	}
}
